"use strict";

import { gsap } from "gsap";

const animationPresets = {
    fade: {
        from: { autoAlpha: 0 },
        to: { autoAlpha: 1 },
    },
    "fade-up": {
        from: { autoAlpha: 0, y: 24 },
        to: { autoAlpha: 1, y: 0 },
    },
    "fade-down": {
        from: { autoAlpha: 0, y: -24 },
        to: { autoAlpha: 1, y: 0 },
    },
    "fade-left": {
        from: { autoAlpha: 0, x: 24 },
        to: { autoAlpha: 1, x: 0 },
    },
    "fade-right": {
        from: { autoAlpha: 0, x: -24 },
        to: { autoAlpha: 1, x: 0 },
    },
    "zoom-in": {
        from: { autoAlpha: 0, scale: 0.96 },
        to: { autoAlpha: 1, scale: 1 },
    },
};

const parseNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
};

const getAnimationPreset = (type) =>
    animationPresets[type] || animationPresets.fade;

const shouldAnimateOnce = (element) =>
    element.dataset.animateOnce !== "false";

const buildAnimationOptions = (element) => ({
    duration: parseNumber(element.dataset.animateDuration, 0.6),
    delay: parseNumber(element.dataset.animateDelay, 0),
    ease: element.dataset.animateEase || "power2.out",
});

const resetElement = (element, preset) => {
    gsap.set(element, {
        ...preset.from,
        willChange: "opacity, transform",
    });
};

const animateElement = (element) => {
    const preset = getAnimationPreset(element.dataset.animate);
    const options = buildAnimationOptions(element);

    gsap.fromTo(
        element,
        preset.from,
        {
            ...preset.to,
            ...options,
            overwrite: "auto",
            onComplete: () => {
                element.style.willChange = "auto";
            },
        }
    );
};

const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const initGsapAnimations = () => {
    const elements = Array.from(
        document.querySelectorAll("[data-animate]")
    );

    if (!elements.length) {
        return;
    }

    if (prefersReducedMotion()) {
        elements.forEach((element) => {
            gsap.set(element, { autoAlpha: 1, clearProps: "transform" });
        });
        return;
    }

    if (!("IntersectionObserver" in window)) {
        elements.forEach((element) => animateElement(element));
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const element = entry.target;
                const preset = getAnimationPreset(element.dataset.animate);
                const once = shouldAnimateOnce(element);

                if (entry.isIntersecting) {
                    animateElement(element);
                    if (once) {
                        observer.unobserve(element);
                    }
                } else if (!once) {
                    resetElement(element, preset);
                }
            });
        },
        {
            threshold: 0.15,
            rootMargin: "0px 0px -10% 0px",
        }
    );

    elements.forEach((element) => {
        if (element.dataset.animateReady) {
            return;
        }

        const preset = getAnimationPreset(element.dataset.animate);
        element.dataset.animateReady = "true";
        resetElement(element, preset);
        observer.observe(element);
    });
};

export default initGsapAnimations;
